import React, { useState } from 'react'
import styled from 'styled-components'

import {
  Button,
  Card as MuiCard,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  Typography,
} from '@material-ui/core'
import {
  Alert,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@material-ui/lab'
import {
  Cancel as CancelIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@material-ui/icons'
import { spacing } from '@material-ui/system'

const Card = styled(MuiCard)(spacing)

const Centered = styled.div`
  text-align: center;
`

export type WorkflowStatus = [string, moment.Moment | null | undefined]

export type WorkflowSequence = WorkflowStatus[]

interface ConfirmationDialogExtraProps {
  error?: string
  prompt: string
  submit?: string
  action?: (props: ActionProps) => void
}

export interface ActionProps {
  complete: () => void
  setConfirmation?: (data: ConfirmationDialogExtraProps) => void
}

export interface Action {
  label: string
  onClick: (props: ActionProps) => void
}

export interface ActionDirection {
  cancel?: Action[]
  forward?: Action[]
  backward?: Action[]
}

interface WorkflowProps {
  title: string
  status: string
  workflow: WorkflowSequence
  loading?: boolean
  actions: { [status: string]: ActionDirection }
}

enum Direction {
  forward = 'Forward',
  backward = 'Backward',
  cancel = 'Cancel',
}

interface ActionPanelProps {
  actions: Action[]
  direction: Direction
  setConfirmation: (data: ConfirmationDialogExtraProps) => void
}

const ActionPanel = (props: ActionPanelProps) => {
  const { actions, direction, setConfirmation } = props
  const [waiting, setWaiting] = useState(false)
  let Icon = ChevronRightIcon
  let side = 'start'
  let color = 'primary'
  switch (direction) {
    case Direction.backward:
      Icon = ChevronLeftIcon
      side = 'start'
      color = 'secondary'
      break
    case Direction.forward:
      Icon = ChevronRightIcon
      side = 'end'
      color = 'primary'
      break
    case Direction.cancel:
      Icon = CancelIcon
      side = 'start'
      color = 'inherit'
      break
  }
  return (
    <Grid container spacing={2}>
      {!!actions.length && (
        <>
          {actions.map((action) => (
            <Grid key={action.label} item xs={12}>
              <Button
                variant="contained"
                color={color as 'primary' | 'secondary' | 'inherit'}
                onClick={() => {
                  setWaiting(true)
                  action.onClick({ complete: () => setWaiting(false), setConfirmation })
                }}
                startIcon={side === 'start' ? <Icon /> : undefined}
                endIcon={side === 'end' ? <Icon /> : undefined}
              >
                {waiting ? 'Processing...' : action.label}
              </Button>
            </Grid>
          ))}
        </>
      )}
    </Grid>
  )
}

interface ConfirmationDialogProps extends ConfirmationDialogExtraProps {
  open: boolean
  onClose: () => void
}

export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
  const { open, onClose, error, prompt, action, submit = 'Save' } = props
  const [waiting, setWaiting] = useState(false)
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography>{prompt}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={() => {
            setWaiting(true)
            action?.({
              complete: () => {
                setWaiting(false)
                onClose()
              },
            })
          }}
        >
          {waiting ? 'Processing...' : submit}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const Workflow = (props: WorkflowProps) => {
  const { title, loading = false, status, workflow, actions } = props
  const [confirmationProps, setConfirmationProps] = useState<ConfirmationDialogExtraProps | null>(
    null
  )
  const statusActions = actions[status] || {}
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h5" component="h2">
          {title}
        </Typography>
        {!!confirmationProps && (
          <ConfirmationDialog
            open={!!confirmationProps}
            onClose={() => setConfirmationProps(null)}
            {...confirmationProps}
          />
        )}
        {loading ? (
          <LinearProgress />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Timeline align="alternate">
                {workflow.map(([status, on], index) => (
                  <TimelineItem key={status}>
                    <TimelineSeparator>
                      <TimelineDot color={on ? 'primary' : 'grey'} />
                      {index + 1 < workflow.length && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography color={on ? 'primary' : 'inherit'}>
                        {status}
                        {on && ` on ${on.format('llll')}`}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Grid>
            <Grid item xs={6} style={{ height: '100%' }}>
              <Centered>
                <Typography variant="h3">Actions</Typography>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <ActionPanel
                          actions={statusActions.backward || []}
                          direction={Direction.backward}
                          setConfirmation={setConfirmationProps}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <ActionPanel
                          actions={statusActions.forward || []}
                          direction={Direction.forward}
                          setConfirmation={setConfirmationProps}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <ActionPanel
                      actions={statusActions.cancel || []}
                      direction={Direction.cancel}
                      setConfirmation={setConfirmationProps}
                    />
                  </Grid>
                </Grid>
              </Centered>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  )
}
